import { useCallback, useEffect, useRef, useState } from 'react';
import { KoobButton, KoobTileButton } from '../../components';
import { StepFrame } from './frame';
import { WizardOverlay } from './overlay';
import { ReviewSummary } from './ReviewSummary';
import { StepList } from './StepList';
import type { StepDef } from './steps';
import type { EntryChoice, TripWizardConfig, WizardValues } from './types';

interface WizardShellProps {
  config: TripWizardConfig;
  steps: StepDef[];
  values: WizardValues;
  set: <K extends keyof WizardValues>(key: K, value: WizardValues[K]) => void;
  patch: (partial: Partial<WizardValues>) => void;
  submit: () => void;
  /** Wipes the answers and starts over on step 1. */
  reset: () => void;
  submitting: boolean;
  error: string | null;
  /** Tiles shown on the entry screen (e.g. From scratch / From templates). */
  entryChoices?: EntryChoice[];
  /** Skip the entry screen and open straight on the first step. */
  startImmediately?: boolean;
}

export function WizardShell({
  config,
  steps,
  values,
  set,
  patch,
  submit,
  reset,
  submitting,
  error,
  entryChoices,
  startImmediately,
}: WizardShellProps) {
  const { t } = config;
  const total = steps.length;
  const hasEntryScreen = !startImmediately;
  // Fresh mount each open: always start on the intro (or first step when the
  // entry screen is skipped), never mid-flow from a previous session.
  const [started, setStarted] = useState(!!startImmediately);
  const [stepIndex, setStepIndex] = useState(0); // total === review page
  // Entry choice showing a panel in place of the tiles (e.g. template picker).
  const [panelChoice, setPanelChoice] = useState<EntryChoice | null>(null);
  // Set from the review screen: which section to open the editing layout on.
  // Editing one answer must not mean walking every step again, so this swaps
  // in the all-fields-on-one-screen layout rather than rewinding stepIndex.
  const [editingStepId, setEditingStepId] = useState<string | null>(null);

  const onChooser = !started && !!entryChoices;

  const onReview = stepIndex === total;
  const onEditing = onReview && editingStepId != null;
  const step = onReview ? null : steps[stepIndex];
  const canContinue = !started || onReview ? true : step!.validate(values);
  const percent = started
    ? Math.round(((stepIndex + 1) / (total + 1)) * 100)
    : 0;

  const advance = useCallback(() => {
    if (panelChoice) return;
    if (!started) {
      setStarted(true);
      return;
    }
    // Editing a section from the review: Enter (and the primary button) go
    // back to the summary. Submitting from here would let a stray Enter in a
    // text field create the trip.
    if (onEditing) {
      setEditingStepId(null);
      return;
    }
    if (onReview) {
      submit();
      return;
    }
    if (canContinue) setStepIndex((i) => Math.min(i + 1, total));
  }, [panelChoice, started, onEditing, onReview, canContinue, submit, total]);

  const back = () => {
    if (onEditing) {
      setEditingStepId(null);
      return;
    }
    if (panelChoice) {
      setPanelChoice(null);
      return;
    }
    if (!started) {
      config.onExit?.();
      return;
    }
    if (stepIndex === 0) {
      if (hasEntryScreen) setStarted(false);
      else config.onExit?.();
    } else setStepIndex((i) => i - 1);
  };

  // Every step lands focus on its first control, so the flow can be walked
  // with the keyboard alone. Fields that focus themselves (the inline
  // country search) already own focus by the time this runs and win.
  const bodyRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const body = bodyRef.current;
    if (!body || body.contains(document.activeElement)) return;
    body
      .querySelector<HTMLElement>(
        'input:not([type="hidden"]), textarea, button:not([disabled])',
      )
      ?.focus();
  }, [started, stepIndex, panelChoice]);

  const handleReset = () => {
    setStarted(!hasEntryScreen);
    setStepIndex(0);
    reset();
  };

  // Enter advances, except while typing in a textarea or a combobox/listbox.
  // (Escape lives in WizardOverlay.)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // An open list swallows the Enter that picks an option (see
      // KoobInputSelect); whatever reaches here is meant for the wizard.
      if (e.key !== 'Enter') return;
      const el = document.activeElement as HTMLElement | null;
      if (el?.tagName === 'TEXTAREA') return;
      e.preventDefault();
      advance();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [advance]);

  return (
    <WizardOverlay config={config}>
      <style>
        {
          '@keyframes koobStepIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}'
        }
      </style>

      {/* progress */}
      <div>
        <div className="h-0.5 w-full bg-gray-100">
          <div
            className="h-full bg-brand transition-all duration-500"
            style={{ width: `${percent}%` }}
          />
        </div>
        <div className="flex justify-between px-6 py-1 text-xs text-gray-400">
          <span>
            {started
              ? onReview
                ? t('review')
                : t('step', { current: stepIndex + 1, total })
              : t('ready')}
          </span>
          <span>{percent}%</span>
        </div>
      </div>

      {/* body */}
      {/* Anchored to the top, not centered: centering re-flows the whole step
          (title included) every time the content grows — an inline dropdown
          opening, a "x selected" line appearing. */}
      {/* min() so short viewports keep the footer on screen: the 44rem floor
          only applies when the panel has room for it (KD: mobile footer). */}
      <div className="flex min-h-[min(44rem,calc(100vh-16rem))] flex-1 items-start justify-center overflow-y-auto px-6 pb-8 pt-8">
        <div
          key={
            started
              ? onEditing
                ? 'editing'
                : onReview
                  ? 'review'
                  : step!.id
              : (panelChoice?.id ?? 'intro')
          }
          ref={bodyRef}
          className="w-full"
          style={{ animation: 'koobStepIn .25s ease' }}
        >
          {!started && panelChoice ? (
            <div className="w-full">
              <h2 className="mb-4 text-center text-xl font-semibold text-gray-900">
                {panelChoice.label}
              </h2>
              {panelChoice.panel!()}
            </div>
          ) : !started ? (
            <div className="mx-auto w-full max-w-xl">
              <h1 className="mt-3 text-4xl font-semibold leading-tight text-gray-900">
                {t('intro.title')}
              </h1>
              <p className="mt-3 text-gray-400">
                {t('intro.subtitle1')}
                <br />
                {t('intro.subtitle2')}
              </p>
              <p className="mt-3 flex items-center gap-2 text-sm text-gray-400">
                <i className="fa-light fa-clock" aria-hidden />
                {t('intro.duration')}
              </p>
              {entryChoices && (
                <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
                  {entryChoices.map((choice) => (
                    <KoobTileButton
                      key={choice.id}
                      icon={choice.icon}
                      onClick={() => {
                        if (choice.panel) setPanelChoice(choice);
                        else if (choice.onSelect) choice.onSelect();
                        else advance();
                      }}
                    >
                      {choice.label}
                    </KoobTileButton>
                  ))}
                </div>
              )}
            </div>
          ) : onEditing ? (
            <StepList
              steps={steps}
              values={values}
              set={set}
              patch={patch}
              config={config}
              focusStepId={editingStepId}
            />
          ) : onReview ? (
            <ReviewSummary
              config={config}
              values={values}
              steps={steps}
              onEditStep={setEditingStepId}
            />
          ) : (
            step && (
              <StepFrame
                number={stepIndex + 1}
                required={step.required}
                title={t(step.titleKey)}
                description={step.descKey ? t(step.descKey) : undefined}
                requiredLabel={t('required')}
                optionalLabel={t('optional')}
              >
                <step.Field
                  values={values}
                  set={set}
                  patch={patch}
                  config={config}
                />
              </StepFrame>
            )
          )}
          {error && (
            <p className="mx-auto mt-4 max-w-xl text-sm text-red-500">
              {error}
            </p>
          )}
        </div>
      </div>

      {/* footer */}
      <div className="flex items-center justify-between border-t border-gray-100 px-6 py-4">
        <div className="flex items-center gap-4">
          <button
            type="button"
            className="border-none bg-transparent text-sm text-gray-500 hover:text-gray-800"
            onClick={back}
            disabled={!started && !panelChoice && !config.onExit}
          >
            ← {t('back')}
          </button>
          {started && (
            <button
              type="button"
              className="border-none bg-transparent text-sm text-gray-400 hover:text-gray-800"
              onClick={handleReset}
            >
              {t('reset')}
            </button>
          )}
        </div>
        <span className="hidden items-center gap-3 text-xs text-gray-400 sm:flex">
          {onChooser ? null : (
            <span>
              {t('pressEnter')}{' '}
              <kbd className="rounded border border-gray-200 px-1">Enter</kbd> ↵
            </span>
          )}
          <span>
            <kbd className="rounded border border-gray-200 px-1">Esc</kbd>{' '}
            {t('escapeCloses')}
          </span>
        </span>
        {onChooser ? (
          <span />
        ) : (
          <KoobButton
            variant="primary"
            loading={submitting}
            disabled={!canContinue}
            onClick={advance}
          >
            {!started
              ? t('intro.start')
              : onEditing
                ? t('review')
                : onReview
                  ? t('summary.create')
                  : t('continue')}{' '}
            →
          </KoobButton>
        )}
      </div>
    </WizardOverlay>
  );
}
